import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveRestore, Loader2 } from 'lucide-react';

import { ListPagination } from '@/components/efs/ListPagination';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';

import type { DeletedDispatch } from './types';
import { useDeletedDispatches, useRestoreDeletedDispatch } from './useDispatch';

interface DeletedDispatchesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const impactText = (row: DeletedDispatch) => {
  const counts = row.impact_counts ?? {};
  const inboundLots = counts.inbound_lots ?? 0;
  const packages = counts.inbound_packages ?? 0;
  const sessions = counts.dismantling_sessions ?? 0;
  const outputs = counts.dismantling_outputs ?? 0;
  const movements = counts.inventory_movements ?? 0;
  return `${inboundLots} / ${packages} / ${sessions} / ${outputs} / ${movements}`;
};

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

/**
 * Platform recycle-bin dialog for dispatch-chain delete archives. The archive
 * snapshot is intentionally never rendered here; operators only see the
 * metadata needed to recover the record during the 30-day retention window.
 */
export const DeletedDispatchesDialog = ({ open, onOpenChange }: DeletedDispatchesDialogProps) => {
  const { t } = useTranslation('operations');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const list = useDeletedDispatches({ page, per_page: perPage }, open);
  const restore = useRestoreDeletedDispatch();
  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const meta = list.data?.meta;
  const total = meta?.total ?? rows.length;

  return (
    <Dialog open={open} onOpenChange={restore.isPending ? undefined : onOpenChange}>
      <DialogContent className="max-h-[min(90vh,900px)] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.dispatch.deleted_dialog.title', {
              defaultValue: '最近削除した配車表',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.dispatch.deleted_dialog.subtitle', {
              defaultValue:
                '削除済みの配車表は30日間だけ復元できます。期限後は復元用データが削除されます。',
            })}
          </DialogDescription>
        </DialogHeader>

        {list.isLoading || list.isFetching ? (
          <div className="flex items-center gap-2 rounded-md border border-efs-border p-4 text-sm text-efs-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('efs.operations.dispatch.deleted_dialog.loading', {
              defaultValue: '削除済みデータを読み込んでいます…',
            })}
          </div>
        ) : list.error ? (
          <LocalizedApiError error={list.error} showTechnicalDetails={false} />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-efs-border p-6 text-center text-sm text-efs-text-muted">
            {t('efs.operations.dispatch.deleted_dialog.empty', {
              defaultValue: '復元可能な削除済み配車表はありません。',
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="overflow-hidden rounded-md border border-efs-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('efs.operations.dispatch.deleted_dialog.columns.codes', {
                        defaultValue: 'コード',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dispatch.deleted_dialog.columns.deleted_at', {
                        defaultValue: '削除日時',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dispatch.deleted_dialog.columns.deleted_by', {
                        defaultValue: '削除者',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dispatch.deleted_dialog.columns.reason', {
                        defaultValue: '理由',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dispatch.deleted_dialog.columns.impact', {
                        defaultValue: '入庫/包装/解体/産出物/在庫',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dispatch.deleted_dialog.columns.deadline', {
                        defaultValue: '復元期限',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dispatch.fields.actions', { defaultValue: '操作' })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const expired = !row.recoverable;
                    return (
                      <TableRow key={row.archive_id}>
                        <TableCell className="text-xs">
                          <div className="font-mono">{row.trip_code || '—'}</div>
                          <div className="font-mono text-efs-text-muted">
                            {row.lot_code || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{formatDateTime(row.deleted_at)}</TableCell>
                        <TableCell className="max-w-[180px] text-xs">
                          <div>{row.deleted_by_display_name}</div>
                          <div className="truncate text-efs-text-muted">{row.deleted_by_email}</div>
                        </TableCell>
                        <TableCell className="max-w-[260px] text-xs">
                          <div className="line-clamp-3">{row.delete_reason}</div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{impactText(row)}</TableCell>
                        <TableCell className="text-xs">
                          <div>{formatDateTime(row.purge_after)}</div>
                          <div className={expired ? 'text-destructive' : 'text-efs-text-muted'}>
                            {expired
                              ? t('efs.operations.dispatch.deleted_dialog.expired', {
                                  defaultValue: '期限切れ',
                                })
                              : t('efs.operations.dispatch.deleted_dialog.days_left', {
                                  count: row.days_remaining,
                                  defaultValue: 'あと {{count}} 日',
                                })}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!row.recoverable || restore.isPending}
                            title={
                              row.recoverable
                                ? undefined
                                : t('efs.operations.dispatch.deleted_dialog.restore_unavailable', {
                                    defaultValue: '復元期限を過ぎているため復元できません。',
                                  })
                            }
                            onClick={() => restore.mutate(row.archive_id)}
                          >
                            <ArchiveRestore className="mr-1 h-3 w-3" aria-hidden />
                            {t('efs.operations.dispatch.deleted_dialog.restore', {
                              defaultValue: '復元',
                            })}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <ListPagination
              page={meta?.page ?? page}
              perPage={meta?.per_page ?? perPage}
              total={total}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPerPageChange={(nextPerPage) => {
                setPage(1);
                setPerPage(PAGE_SIZE_OPTIONS.includes(nextPerPage) ? nextPerPage : DEFAULT_PER_PAGE);
              }}
            />
          </div>
        )}

        {restore.error ? (
          <LocalizedApiError error={restore.error} showTechnicalDetails={false} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default DeletedDispatchesDialog;
